import { Component, OnInit, inject, ChangeDetectionStrategy, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ConnectionTestService } from '../../shared/services/connection-test/connection-test.service';
import { ConnectionTestResult, TestObjectStorageRequest } from '@app/shared/interfaces';

/** Standard connection: tested by its saved id (`POST /connection/{id}/test`). */
interface StandardConnectionTestData {
  connectionName: string;
  projectId: number;
  connectionId: number;
}

/** Object storage: tested by posting its (unsaved) config (`POST /object-storage-test`). */
interface ObjectStorageConnectionTestData {
  connectionName: string;
  projectId: number;
  objectStorageRequest: TestObjectStorageRequest;
}

/**
 * Exactly one test mode: a saved standard connection (by id) or an unsaved
 * object-storage configuration (by payload). Modeled as a union so passing
 * neither — or both — is a compile error rather than silent undefined behavior.
 */
export type ConnectionTestingDialogData =
  | StandardConnectionTestData
  | ObjectStorageConnectionTestData;

type TestingState = 'loading' | 'success' | 'error';

@Component({
  selector: 'app-connection-testing-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './connection-testing-dialog.component.html',
  styleUrls: ['./connection-testing-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectionTestingDialogComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<ConnectionTestingDialogComponent>>(MatDialogRef);
  data = inject<ConnectionTestingDialogData>(MAT_DIALOG_DATA);
  private connectionTestService = inject(ConnectionTestService);
  private destroyRef = inject(DestroyRef);

  state = signal<TestingState>('loading');
  successMessage = signal('');
  errorMessage = signal('');

  ngOnInit(): void {
    this.runTest();
  }

  private runTest(): void {
    const data = this.data;
    const connectionName = data.connectionName;

    let test$: Observable<ConnectionTestResult>;
    if ('objectStorageRequest' in data) {
      test$ = this.connectionTestService.testObjectStorage(
        data.projectId,
        data.objectStorageRequest,
      );
    } else if (data.connectionId) {
      test$ = this.connectionTestService.testStandard(
        data.projectId,
        data.connectionId,
      );
    } else {
      // No saved id and no config — nothing to test. Surface an error rather
      // than calling the API with an invalid id.
      this.state.set('error');
      this.errorMessage.set(
        `Unable to test connection ${connectionName}: missing connection details`,
      );
      return;
    }

    test$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (result.success) {
            this.state.set('success');
            this.successMessage.set(
              result.message ??
              (result.filesCount !== undefined
                ? `Connection successful, Found ${new Intl.NumberFormat().format(result.filesCount)} files in bucket`
                : 'Connection successful'),
            );
          } else {
            this.state.set('error');
            this.errorMessage.set(result.message ?? 'Connection test failed');
          }
        },
        error: (err) => {
          this.state.set('error');
          this.errorMessage.set(
            err?.error?.message ??
            err?.message ??
            `Failed to test connection ${connectionName}`,
          );
        },
      });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
